import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { 
  ChevronDown, 
  Search, 
  Plus, 
  Check, 
  Tag,
  Loader2,
  X
} from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useCategories } from '../context/CategoryContext';
import { Category } from '../types';

interface CategorySelectProps {
  value: string;
  onChange: (category: string) => void;
  placeholder?: string;
  className?: string;
}

export function CategorySelect({ value, onChange, placeholder = "Select or create category", className = "" }: CategorySelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [creating, setCreating] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Use global category context
  const { categories, loading, createCategory } = useCategories();

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleCreateCategory = async () => {
    if (!searchTerm.trim()) return;

    try {
      setCreating(true);
      // Convert category name to uppercase before creating
      const categoryName = searchTerm.trim().toUpperCase();
      const newCategory = await createCategory(categoryName);
      onChange(newCategory.name);
      setIsOpen(false);
      setSearchTerm('');
      toast.success(`Category "${newCategory.name}" created successfully`);
    } catch (error) {
      console.error('Error creating category:', error);
      if (error instanceof Error && error.message === 'Category already exists') {
        toast.error('Category already exists');
      } else {
        toast.error('Failed to create category');
      }
    } finally {
      setCreating(false);
    }
  };

  const handleSelectCategory = (categoryName: string) => {
    onChange(categoryName);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClearCategory = () => {
    onChange('');
    setIsOpen(false);
    setSearchTerm('');
  };

  const filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const exactMatch = filteredCategories.find(
    category => category.name.toLowerCase() === searchTerm.trim().toLowerCase()
  );

  const showCreateOption = searchTerm.trim() && !exactMatch;

  return (
    <div ref={dropdownRef} className={`relative ${className}`} style={{ zIndex: 1100 }}>
      {/* Selected Value Display / Trigger */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white cursor-pointer hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-jamaica-green/20 focus:border-jamaica-green transition-colors"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Tag className="w-4 h-4 text-gray-400 shrink-0" />
          {value ? (
            <Badge variant="outline" className="text-xs">
              {value}
            </Badge>
          ) : (
            <span className="text-gray-500 truncate">{placeholder}</span>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </div>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute z-[10100] w-full mt-1 bg-white border border-gray-300 rounded-md shadow-xl max-h-60 overflow-hidden"
            style={{
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
          >
            {/* Search Input */}
            <div className="p-2 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  ref={inputRef}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search categories..."
                  className="pl-9 text-sm border-0 focus:ring-0 focus:border-0"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && showCreateOption) {
                      e.preventDefault();
                      handleCreateCategory();
                    }
                  }}
                />
              </div>
            </div>

            {/* Options List */}
            <div className="max-h-48 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-4 h-4 animate-spin text-jamaica-green" />
                  <span className="ml-2 text-sm text-gray-600">Loading categories...</span>
                </div>
              ) : (
                <>
                  {/* Clear Category Option */}
                  {value && (
                    <button
                      type="button"
                      onClick={handleClearCategory}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-red-50 focus:bg-red-50 focus:outline-none flex items-center gap-2 border-b border-gray-100"
                    >
                      <X className="w-4 h-4 text-red-500" />
                      <span className="text-red-600 font-medium">
                        Clear Category
                      </span>
                    </button>
                  )}

                  {/* Create New Option */}
                  {showCreateOption && (
                    <button
                      type="button"
                      onClick={handleCreateCategory}
                      disabled={creating}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-jamaica-green/10 focus:bg-jamaica-green/10 focus:outline-none flex items-center gap-2 border-b border-gray-100"
                    >
                      {creating ? (
                        <Loader2 className="w-4 h-4 animate-spin text-jamaica-green" />
                      ) : (
                        <Plus className="w-4 h-4 text-jamaica-green" />
                      )}
                      <span className="text-jamaica-green font-medium">
                        Create "{searchTerm.trim().toUpperCase()}"
                      </span>
                    </button>
                  )}

                  {/* Existing Categories */}
                  {filteredCategories.length > 0 ? (
                    filteredCategories.map((category) => (
                      <button
                        key={category.id}
                        type="button"
                        onClick={() => handleSelectCategory(category.name)}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 focus:bg-gray-50 focus:outline-none flex items-center justify-between group"
                      >
                        <span className="truncate">{category.name}</span>
                        {value === category.name && (
                          <Check className="w-4 h-4 text-jamaica-green" />
                        )}
                      </button>
                    ))
                  ) : !showCreateOption && !value ? (
                    <div className="px-3 py-4 text-sm text-gray-500 text-center">
                      {searchTerm ? 'No categories found' : 'No categories available'}
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
} 